import React from "react";
import DateFilter from "../DateFilter";
import PageHeader from "../PageHeader";

// Loading Card Component
const LoadingCard = () => (
  <div className="bg-white rounded-lg border border-gray-300 p-4">
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
      <div className="space-y-3">
        <div className="h-8 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  </div>
);

// Loading Table Component
const LoadingTable = ({ variant = "default" }) => {
  const getColumnCount = () => {
    switch (variant) {
      case "orders":
        return 10;
      case "overview":
        return 5;
      default:
        return 6;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-300">
      <div className="animate-pulse p-4">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-4">
          {[...Array(8)].map((_, idx) => (
            <div key={idx} className="flex gap-4">
              {[...Array(getColumnCount())].map((_, colIdx) => (
                <div
                  key={colIdx}
                  className="h-4 bg-gray-200 rounded flex-1"
                ></div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Loading Chart Component
const LoadingChart = () => (
  <div className="bg-white rounded-lg border border-gray-300 p-4">
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
      <div className="h-[300px] bg-gray-100 rounded-lg"></div>
    </div>
  </div>
);

// Loading Tabs Component
const LoadingTabs = () => (
  <div className="animate-pulse flex items-center gap-2 bg-gray-100 p-2 rounded-lg">
    {[...Array(3)].map((_, idx) => (
      <div
        key={idx}
        className="h-8 bg-gray-200 rounded flex-1 max-w-[120px]"
      ></div>
    ))}
  </div>
);

const ReportLoadingState = ({
  dateType,
  startDate,
  endDate,
  onDateChange,
  variant = "default", // 'default', 'overview', 'menu', 'items', 'orders'
  title = "Report",
  description = "Loading report data...",
}) => {
  const renderStatsCards = () => {
    const cardCount = {
      default: 4,
      overview: 3,
      menu: 4,
      items: 4,
      orders: 3,
    }[variant];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(cardCount)].map((_, idx) => (
          <LoadingCard key={idx} />
        ))}
      </div>
    );
  };

  const renderCharts = () => {
    switch (variant) {
      case "overview":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1">
              <LoadingChart />
            </div>
            <div className="lg:col-span-1">
              <LoadingChart />
            </div>
            <div className="lg:col-span-1">
              <LoadingChart />
            </div>
          </div>
        );
      case "menu":
      case "items":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LoadingChart />
            <LoadingChart />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Page Header with Loading State */}
      <PageHeader
        title={title}
        description={description}
        rightSlot={
          <DateFilter
            dateType={dateType}
            startDate={startDate}
            endDate={endDate}
            onDateChange={onDateChange}
          />
        }
        mobileDropdownContent={
          <DateFilter
            dateType={dateType}
            startDate={startDate}
            endDate={endDate}
            onDateChange={onDateChange}
          />
        }
      />

      {/* Stats Cards */}
      {renderStatsCards()}

      {/* Tabs for Orders variant */}
      {variant === "orders" && <LoadingTabs />}

      {/* Charts */}
      {renderCharts()}

      {/* Table */}
      <LoadingTable variant={variant} />

      {/* Pagination Skeleton */}
      <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-300">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
        <div className="flex items-center space-x-2">
          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
          <div className="flex space-x-1">
            {[...Array(3)].map((_, idx) => (
              <div
                key={idx}
                className="h-8 w-8 bg-gray-200 rounded animate-pulse"
              ></div>
            ))}
          </div>
          <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default ReportLoadingState;
